import Raven from 'raven-js';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
class ManualPromise {
  constructor() {
    this.settled = false;
    this.promise = new Promise((resolve, reject) => {
      this.resolvePromise = resolve;
      this.rejectPromise = reject;
    }).finally(() => {
      this.settled = true;
    });
  }
  resolve(value) {
    if (!this.resolvePromise) {
      throw new Error('Promise not initialized');
    }
    this.resolvePromise(value);
  }
  reject(reason) {
    if (!this.rejectPromise) {
      throw new Error('Promise not initialized');
    }
    this.rejectPromise(reason);
  }
}
export const ResultPromiseContext = /*#__PURE__*/createContext(null);
export function useResultPromiseRef() {
  return useContext(ResultPromiseContext).resultPromiseRef;
}
export function useResultPromiseInitializer() {
  const {
    resultPromiseRef,
    queuedResultRef
  } = useContext(ResultPromiseContext);
  return useCallback(() => {
    resultPromiseRef.current = new ManualPromise();
    queuedResultRef.current = null;
    return resultPromiseRef.current.promise;
  }, [queuedResultRef, resultPromiseRef]);
}

// The functions returned by this hook should be used to queue a response to
// the promise returned to the host app. When the modal closes, the promise
// will be automatically settled into the state that has been queued.
export function useResultPromiseResolvers() {
  const {
    queuedResultRef
  } = useContext(ResultPromiseContext);
  const queueResolve = useCallback(value => {
    if (queuedResultRef.current !== null) {
      Raven.captureException(new Error('Unexpected result promise resolve - another resolution has already been queued.'));
    }
    queuedResultRef.current = {
      state: 'resolved',
      value
    };
  }, [queuedResultRef]);
  const queueReject = useCallback(reason => {
    if (queuedResultRef.current !== null) {
      Raven.captureException(new Error('Unexpected result promise reject - another resolution has already been queued.'));
    }
    queuedResultRef.current = {
      state: 'rejected',
      reason
    };
  }, [queuedResultRef]);
  return {
    queueResolve,
    queueReject
  };
}

// The function returned by this hook ensures that the result promise will be
// settled into either the queued state or an unhandled, rejected state
export function useSettleResultPromise() {
  const {
    resultPromiseRef,
    queuedResultRef
  } = useContext(ResultPromiseContext);
  return useCallback(() => {
    if (resultPromiseRef.current === null) {
      const error = new Error('Unexpected modal close - the modal was closed without initializing the returned promise.');
      resultPromiseRef.current.reject(error);
      Raven.captureException(error);
    } else if (queuedResultRef.current === null) {
      const error = new Error('Unhandled modal close - the modal was closed without settling the returned promise.');
      resultPromiseRef.current.reject(error);
      Raven.captureException(error);
    } else {
      if (queuedResultRef.current.state === 'resolved') {
        resultPromiseRef.current.resolve(queuedResultRef.current.value);
      } else if (queuedResultRef.current.state === 'rejected') {
        resultPromiseRef.current.reject(queuedResultRef.current.reason);
      }
    }
  }, [queuedResultRef, resultPromiseRef]);
}
export const ResultPromiseContextProvider = ({
  children
}) => {
  const resultPromiseRef = useRef(null);
  const queuedResultRef = useRef(null);
  const value = useMemo(() => ({
    resultPromiseRef,
    queuedResultRef
  }), []);
  return /*#__PURE__*/_jsx(ResultPromiseContext.Provider, {
    value: value,
    children: children
  });
};